import { SearchIcon } from 'lucide-react-native';
import * as React from 'react';
import { ActivityIndicator, Pressable, TextInput, View } from 'react-native';

import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import { authedFetch } from '@/lib/authed-fetch';
import type { PlaceSelection } from '@/lib/events';
import type { PlacePrediction } from '@/lib/integrations/places';

type EventPlaceSearchProps = Readonly<{
  onSelect: (place: PlaceSelection) => void;
  validationError?: string | null;
}>;

export function EventPlaceSearch({ onSelect, validationError }: EventPlaceSearchProps) {
  const [query, setQuery] = React.useState('');
  const [predictions, setPredictions] = React.useState<PlacePrediction[]>([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const trimmed = query.trim();
    let active = true;
    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      if (trimmed.length < 3) {
        setPredictions([]);
        setError(null);
        return;
      }

      setIsSearching(true);
      try {
        const response = await authedFetch(
          `/api/places/autocomplete?q=${encodeURIComponent(trimmed)}`,
          { signal: controller.signal }
        );
        const body = (await response.json()) as { predictions?: PlacePrediction[]; error?: string };
        if (!active) return;
        if (!response.ok) throw new Error(body.error ?? 'Search failed');
        setPredictions(body.predictions ?? []);
        setError(null);
      } catch (searchError) {
        if (active && !(searchError instanceof Error && searchError.name === 'AbortError')) {
          setError(searchError instanceof Error ? searchError.message : 'Search failed');
          setPredictions([]);
        }
      } finally {
        if (active) setIsSearching(false);
      }
    }, 300);

    return () => {
      active = false;
      controller.abort();
      clearTimeout(timeout);
    };
  }, [query]);

  async function selectPrediction(placeId: string) {
    setIsSearching(true);
    setError(null);
    try {
      const response = await authedFetch(
        `/api/places/details?place_id=${encodeURIComponent(placeId)}`
      );
      const body = (await response.json()) as { place?: PlaceSelection; error?: string };
      if (!response.ok || !body.place) {
        throw new Error(body.error ?? 'Could not load place');
      }
      onSelect(body.place);
    } catch (detailsError) {
      setError(detailsError instanceof Error ? detailsError.message : 'Could not load place');
    } finally {
      setIsSearching(false);
    }
  }

  return (
    <View className="gap-3">
      <View className="min-h-12 flex-row items-center gap-3 border-b border-border px-1">
        <Icon as={SearchIcon} className="size-5 text-muted-foreground" />
        <TextInput
          accessibilityLabel="Search locations"
          autoCapitalize="none"
          autoCorrect={false}
          className="min-h-12 flex-1 font-body text-base text-foreground outline-none"
          onChangeText={setQuery}
          placeholder="Search for a place"
          placeholderTextColor="#71717A"
          value={query}
        />
        {isSearching ? <ActivityIndicator /> : null}
      </View>

      {validationError ? (
        <Text className="text-sm text-destructive" selectable>
          {validationError}
        </Text>
      ) : null}

      {error ? (
        <Text className="text-sm text-destructive" selectable>
          {error}
        </Text>
      ) : null}

      <Text variant="muted">Results are limited to Thailand.</Text>

      {query.trim().length > 0 && query.trim().length < 3 ? (
        <Text variant="muted">Enter at least three characters.</Text>
      ) : null}

      {!isSearching && query.trim().length >= 3 && predictions.length === 0 && !error ? (
        <Text variant="muted">No matching places.</Text>
      ) : null}

      <View>
        {predictions.map((prediction) => (
          <Pressable
            accessibilityRole="button"
            className="min-h-16 justify-center border-b border-border py-3 active:opacity-60"
            key={prediction.placeId}
            onPress={() => void selectPrediction(prediction.placeId)}
          >
            <Text>{prediction.primaryText}</Text>
            {prediction.secondaryText ? (
              <Text variant="muted" numberOfLines={2}>
                {prediction.secondaryText}
              </Text>
            ) : null}
          </Pressable>
        ))}
      </View>
    </View>
  );
}
